import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { sanitizeSvg } from "@/lib/sanitize";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Polygon, CircleNotch, Image as ImageIcon, ArrowRight, CheckCircle, Warning, FilePdf, FileCsv } from "@phosphor-icons/react";

const clean = (svg) => sanitizeSvg(svg);

export const LotStudio = ({ onUsePrompt, clientName, clientStandards }) => {
  const [legal, setLegal] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [proj, setProj] = useState({ project: "", parcel: "", prepared_by: "", job_no: "" });
  const [adjust, setAdjust] = useState(false);
  const [method, setMethod] = useState("compass");
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const rd = clientStandards?.report_defaults;
    if (rd) setProj({ project: rd.project || "", parcel: rd.parcel || "", prepared_by: rd.prepared_by || "", job_no: rd.job_no || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const plat = useRef();
  const recalcTimer = useRef();

  const recompute = (courses) => {
    clearTimeout(recalcTimer.current);
    setRecalcing(true);
    recalcTimer.current = setTimeout(async () => {
      try {
        const cleaned = courses.map((c) => ({
          call: c.call, curve: !!c.curve, curve_dir: c.curve_dir || "",
          az: Number(c.az) || 0, dist: Number(c.dist) || 0, radius: Number(c.radius) || 0,
        }));
        const r = await api.recomputeLot(cleaned);
        setData((cur) => cur ? { ...cur, metrics: r.metrics } : cur);
      } catch { toast.error("Couldn't recompute the lot — showing the last values."); } finally { setRecalcing(false); }
    }, 450);
  };

  const editCourse = (i, field, value) => {
    setData((prev) => {
      if (!prev) return prev;
      const courses = prev.courses.map((c, idx) => idx === i ? { ...c, [field]: value } : c);
      recompute(courses);
      return { ...prev, courses };
    });
  };

  const toggleCurve = (i) => {
    setData((prev) => {
      if (!prev) return prev;
      const courses = prev.courses.map((c, idx) => idx === i ? { ...c, curve: !c.curve, curve_dir: c.curve_dir || "R" } : c);
      recompute(courses);
      return { ...prev, courses };
    });
  };

  const previewLegal = async () => {
    if (!legal.trim()) { toast.error("Paste a legal description first."); return; }
    setBusy(true);
    try { setData(await api.parseLegal(legal)); }
    catch { toast.error("Could not parse the legal description."); }
    finally { setBusy(false); }
  };

  const previewPlat = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const t = toast.loading("Reading plat image (vision)…");
    try { setData(await api.readPlat(file)); toast.success("Plat read", { id: t }); }
    catch { toast.error("Could not read the plat image.", { id: t }); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const useForGen = () => {
    if (!data?.courses?.length) return;
    const lines = data.courses.map((c, i) =>
      `${i + 1}. ${c.call || ""} -> azimuth ${Number(c.az).toFixed(4)}° CW, ${c.curve ? `CURVE chord ${c.dist} ft, radius ${c.radius} ft, turning ${c.curve_dir === "L" ? "left/CCW" : "right/CW"} (draw as an ARC)` : `distance ${c.dist} ft`}`).join("\n");
    const prompt =
      `Draw this parcel/lot on layer BOUNDARY using these courses (azimuths are decimal degrees from North, CLOCKWISE around the parcel). ` +
      `Start from a user-picked point, draw straight courses with polar/angle and curve courses as true ARCs (use the given chord bearing/length and radius and turn direction), label each with bearing (angtos surveyor units) + distance, close the figure, and report area and closure.\n\nCourses:\n${lines}`;
    onUsePrompt(prompt);
    setOpen(false);
    toast.success("Lot courses sent to the generator prompt");
  };

  const m = data?.metrics;

  const downloadReport = async () => {
    if (!data?.courses?.length) return;
    setReporting(true);
    const t = toast.loading("Building closure report…");
    try {
      const courses = data.courses.map((c) => ({
        call: c.call, curve: !!c.curve, curve_dir: c.curve_dir || "",
        az: Number(c.az) || 0, dist: Number(c.dist) || 0, radius: Number(c.radius) || 0,
      }));
      const project = { ...proj, firm: clientName || "", logo: clientStandards?.branding?.logo || "" };
      const blob = await api.lotReportPdf({ courses, metrics: m, project, adjust, method });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "closure_report.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Closure report downloaded", { id: t });
    } catch { toast.error("Could not build the report.", { id: t }); }
    finally { setReporting(false); }
  };

  const downloadAdjCsv = async () => {
    if (!data?.courses?.length) return;
    const t = toast.loading("Building adjusted-courses CSV…");
    try {
      const courses = data.courses.map((c) => ({
        curve: !!c.curve, curve_dir: c.curve_dir || "",
        az: Number(c.az) || 0, dist: Number(c.dist) || 0, radius: Number(c.radius) || 0,
      }));
      const blob = await api.lotAdjustmentCsv({ courses, method });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "adjusted_courses.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Adjusted courses CSV downloaded", { id: t });
    } catch { toast.error("Could not build the CSV.", { id: t }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="lot-studio-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Polygon size={14} weight="bold" /> Lot Studio
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="lot-studio-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Polygon size={18} weight="bold" className="text-[var(--primary)]" />Lot Studio — legal description & plat preview</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Parse a legal description or plat image into courses, fix mis-reads, and send them to the generator.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Textarea value={legal} onChange={(e) => setLegal(e.target.value)} data-testid="legal-input"
              placeholder="Paste a legal description… e.g. 'thence N 45-30-20 E 120.50 feet; thence …'"
              className="min-h-[180px] resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[12px]" />
            <div className="flex gap-2">
              <Button size="sm" onClick={previewLegal} disabled={busy} data-testid="preview-legal-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white flex-1">
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} />} Preview from Legal
              </Button>
              <input type="file" accept="image/*" ref={plat} className="hidden" onChange={previewPlat} data-testid="plat-file-input" />
              <Button size="sm" variant="outline" onClick={() => plat.current.click()} disabled={busy} data-testid="upload-plat-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]">
                <ImageIcon size={14} /> Plat image
              </Button>
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] p-3 flex flex-col items-center justify-center min-h-[180px]" data-testid="lot-preview">
            {m?.svg ? <div className="[&_svg]:max-w-full [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: clean(m.svg) }} />
              : <span className="text-[var(--text-dim)] text-xs">Preview appears here</span>}
          </div>
        </div>
        {m && (
          <div className="flex items-center gap-4 text-[12px] font-mono flex-wrap" data-testid="lot-metrics">
            {recalcing && <span className="text-[var(--primary)] flex items-center gap-1" data-testid="lot-recalcing"><CircleNotch size={12} className="animate-spin" />updating…</span>}
            <span className={m.closed ? "text-[var(--success)]" : "text-[var(--alert)]"}>
              {m.closed ? <CheckCircle size={13} className="inline mr-1" /> : <Warning size={13} className="inline mr-1" />}
              {m.closed ? "Closed" : "Not closed"}
            </span>
            <span className="text-[var(--text-dim)]">Courses: <b className="text-[var(--text)]">{m.num_courses}</b></span>
            <span className="text-[var(--text-dim)]">Closure: <b className="text-[var(--text)]">{m.closure_ft} ft</b></span>
            <span className="text-[var(--text-dim)]">Area: <b className="text-[var(--text)]">{m.area_sqft} ft² ({m.area_acres} ac)</b></span>
            <span className="text-[var(--text-dim)]">Perimeter: <b className="text-[var(--text)]">{m.perimeter_ft} ft</b></span>
          </div>
        )}
        {data?.courses?.length > 0 && (
          <div className="border border-[var(--border)] rounded-[3px] max-h-[130px] overflow-auto" data-testid="courses-table">
            <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] bg-[var(--surface-2)] sticky top-0 grid grid-cols-[20px_1fr_78px_64px_40px_64px] gap-1.5">
              <span>#</span><span>call — edit to fix mis-reads</span><span>az °</span><span>dist</span><span>arc</span><span>radius</span>
            </div>
            {data.courses.map((c, i) => (
              <div key={i} className="grid grid-cols-[20px_1fr_78px_64px_40px_64px] gap-1.5 px-2 py-1 text-[11px] font-mono border-t border-[var(--border)]/40 items-center" data-testid={`course-row-${i}`}>
                <span className="text-[var(--warn)]">{i + 1}</span>
                <span className="truncate text-[var(--text-dim)]">{c.call || "—"}</span>
                <Input value={c.az ?? ""} onChange={(e) => editCourse(i, "az", e.target.value)} data-testid={`course-az-${i}`}
                  className="h-6 px-1 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" title="Azimuth °" />
                <Input value={c.dist ?? ""} onChange={(e) => editCourse(i, "dist", e.target.value)} data-testid={`course-dist-${i}`}
                  className="h-6 px-1 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" title="Chord/Distance ft" />
                <button onClick={() => toggleCurve(i)} data-testid={`course-curve-${i}`} title="Toggle straight/arc"
                  className={`h-6 rounded-[2px] border text-[10px] ${c.curve ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>
                  {c.curve ? (c.curve_dir === "L" ? "L⌒" : "R⌒") : "—"}
                </button>
                <Input value={c.curve ? (c.radius ?? "") : ""} disabled={!c.curve} onChange={(e) => editCourse(i, "radius", e.target.value)} data-testid={`course-radius-${i}`}
                  className="h-6 px-1 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px] disabled:opacity-40" title="Arc radius ft" placeholder="R" />
              </div>
            ))}
          </div>
        )}
        {data?.courses?.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="lot-report-panel">
            <div className="grid grid-cols-4 gap-2">
              <Input value={proj.project} onChange={(e) => setProj((p) => ({ ...p, project: e.target.value }))} data-testid="report-project"
                placeholder="Project" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <Input value={proj.parcel} onChange={(e) => setProj((p) => ({ ...p, parcel: e.target.value }))} data-testid="report-parcel"
                placeholder="Parcel / Lot" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <Input value={proj.job_no} onChange={(e) => setProj((p) => ({ ...p, job_no: e.target.value }))} data-testid="report-jobno"
                placeholder="Job No." className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <Input value={proj.prepared_by} onChange={(e) => setProj((p) => ({ ...p, prepared_by: e.target.value }))} data-testid="report-preparedby"
                placeholder="Prepared by" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
            </div>
            <label className="flex items-center gap-2 text-[11px] font-mono text-[var(--text-dim)] cursor-pointer" data-testid="report-adjust-label">
              <input type="checkbox" checked={adjust} onChange={(e) => setAdjust(e.target.checked)} data-testid="report-adjust" className="accent-[var(--primary)]" />
              Balance the traverse (show before/after + per-course corrections)
              {adjust && (
                <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="report-method"
                  className="h-6 ml-1 px-1 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[10px] text-[var(--text)]">
                  <option value="compass">Compass-rule (Bowditch)</option>
                  <option value="transit">Transit-rule</option>
                </select>
              )}
            </label>
            <div className="flex gap-2">
              <Button onClick={useForGen} data-testid="use-lot-btn"
                className="flex-1 h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                <ArrowRight size={15} /> Use these courses to generate LISP
              </Button>
              <Button onClick={downloadReport} disabled={reporting} variant="outline" data-testid="lot-report-btn"
                className="h-9 rounded-[3px] gap-2 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] text-[var(--text)]">
                {reporting ? <CircleNotch size={15} className="animate-spin" /> : <FilePdf size={15} />} Closure report (PDF)
              </Button>
              {adjust && (
                <Button onClick={downloadAdjCsv} variant="outline" data-testid="lot-adjcsv-btn"
                  className="h-9 rounded-[3px] gap-2 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] text-[var(--text)]">
                  <FileCsv size={15} /> Adjusted CSV
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
